/**
 * Cover 2 Coverage Rules
 * CBs play flat zones, Safeties play deep halves
 * Requirements: 7.1, 7.3
 */

import { CoverageDefinition, CoverageReaction, Position, DBLBReaction, FSReaction, CB2Reaction, DBLB2Reaction, SSReaction, LBReaction } from '../types';

/**
 * Starting position for CB1 in Cover 2
 * CB plays near the line, responsible for flat zone
 */
const CB1_COVER2_START: Position = { x: 15, y: 78 };

/**
 * Starting position for DB/LB1 (hook/curl defender)
 */
const DBLB1_COVER2_START: Position = { x: 30, y: 72 };

/**
 * Starting position for FS in Cover 2
 * Deep half safety - responsible for deep left half
 */
const FS_COVER2_START: Position = { x: 30, y: 45 };

/**
 * Starting position for CB2 in Cover 2 (mirrors CB1)
 */
const CB2_COVER2_START: Position = { x: 85, y: 78 };

/**
 * Starting position for DB/LB2 (mirrors DB/LB1)
 */
const DBLB2_COVER2_START: Position = { x: 70, y: 72 };

/**
 * Starting position for SS in Cover 2
 * Deep half safety - responsible for deep right half
 */
const SS_COVER2_START: Position = { x: 70, y: 45 };

/**
 * Starting position for LB in Cover 2
 * Middle zone defender
 */
const LB_COVER2_START: Position = { x: 50, y: 70 };

/**
 * Cover 2 CB1 reactions
 * CB plays flat zone - jumps any short outside routes, funnels inside
 */
const cover2Reactions: CoverageReaction[] = [
  {
    routeTrigger: 'slant',
    wrPositionTrigger: { x: 20, y: 75 },
    cbResponse: [
      { x: 18, y: 72 },  // jam/funnel WR inside
      { x: 12, y: 65 },  // settle in flat zone
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'out',
    wrPositionTrigger: { x: 20, y: 70 },
    cbResponse: [
      { x: 15, y: 72 },  // read out route
      { x: 8, y: 68 },   // jump the out route
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'deepOut',
    wrPositionTrigger: { x: 20, y: 45 },
    cbResponse: [
      { x: 15, y: 70 },  // sink with WR
      { x: 10, y: 55 },  // carry to flat zone boundary
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'deepIn',
    wrPositionTrigger: { x: 20, y: 45 },
    cbResponse: [
      { x: 18, y: 70 },  // funnel inside
      { x: 12, y: 60 },  // settle in flat
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'comeback',
    wrPositionTrigger: { x: 20, y: 50 },
    cbResponse: [
      { x: 15, y: 68 },  // sink
      { x: 12, y: 55 },  // carry deep in flat
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'go',
    wrPositionTrigger: { x: 20, y: 70 },
    cbResponse: [
      { x: 18, y: 70 },  // jam at line
      { x: 15, y: 55 },  // sink to flat zone boundary, pass off to safety
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'post',
    wrPositionTrigger: { x: 20, y: 55 },
    cbResponse: [
      { x: 18, y: 70 },  // funnel inside
      { x: 12, y: 58 },  // settle in flat
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'corner',
    wrPositionTrigger: { x: 20, y: 55 },
    cbResponse: [
      { x: 15, y: 68 },  // sink with WR
      { x: 10, y: 50 },  // carry to flat zone boundary
    ],
    responseDelay: 0,
  },
  {
    // Drag: Shallow cross - CB plays underneath in flat
    routeTrigger: 'drag',
    wrPositionTrigger: { x: 20, y: 80 },
    cbResponse: [
      { x: 15, y: 75 },  // read release
      { x: 12, y: 68 },  // settle in flat zone
    ],
    responseDelay: 0,
  },
  {
    // Hitch: 5-yard stop - CB jumps the hitch
    routeTrigger: 'hitch',
    wrPositionTrigger: { x: 20, y: 70 },
    cbResponse: [
      { x: 15, y: 72 },  // read
      { x: 10, y: 68 },  // break on hitch
    ],
    responseDelay: 0,
  },
  {
    // Curl: 10-yard stop - CB sinks in flat
    routeTrigger: 'curl',
    wrPositionTrigger: { x: 20, y: 55 },
    cbResponse: [
      { x: 15, y: 68 },  // sink
      { x: 12, y: 55 },  // carry to flat zone boundary
    ],
    responseDelay: 0,
  },
];

/**
 * DB/LB1 reactions - hook/curl zone defender
 */
const cover2DBLB1Reactions: DBLBReaction[] = [
  {
    routeTrigger: 'slant',
    response: [
      { x: 30, y: 65 },  // drop to hook zone
      { x: 35, y: 55 },  // collision/carry slant
    ],
  },
  {
    routeTrigger: 'out',
    response: [
      { x: 28, y: 65 },  // drop
      { x: 25, y: 58 },  // settle in hook zone
    ],
  },
  {
    routeTrigger: 'deepOut',
    response: [
      { x: 28, y: 62 },  // drop
      { x: 25, y: 50 },  // sink to curl zone
    ],
  },
  {
    routeTrigger: 'deepIn',
    response: [
      { x: 30, y: 62 },  // drop
      { x: 40, y: 48 },  // carry dig route
    ],
  },
  {
    routeTrigger: 'comeback',
    response: [
      { x: 28, y: 60 },  // drop
      { x: 22, y: 48 },  // sink to curl
    ],
  },
  {
    routeTrigger: 'go',
    response: [
      { x: 30, y: 62 },  // drop
      { x: 30, y: 50 },  // settle in hook zone
    ],
  },
  {
    routeTrigger: 'post',
    response: [
      { x: 30, y: 62 },  // drop
      { x: 38, y: 50 },  // carry post underneath
    ],
  },
  {
    routeTrigger: 'corner',
    response: [
      { x: 28, y: 62 },  // drop
      { x: 22, y: 50 },  // sink to curl
    ],
  },
  {
    // Drag: Shallow cross - drop to hook zone, collision
    routeTrigger: 'drag',
    response: [
      { x: 30, y: 65 },
      { x: 35, y: 55 },
    ],
  },
  {
    // Hitch: 5-yard stop - drop to hook zone
    routeTrigger: 'hitch',
    response: [
      { x: 28, y: 65 },
      { x: 25, y: 58 },
    ],
  },
  {
    // Curl: 10-yard stop - sink to curl zone, close on turn
    routeTrigger: 'curl',
    response: [
      { x: 28, y: 62 },
      { x: 24, y: 50 },
    ],
  },
];

/**
 * FS reactions - deep half safety (left side)
 */
const cover2FSReactions: FSReaction[] = [
  {
    routeTrigger: 'slant',
    response: [
      { x: 30, y: 35 },  // backpedal
      { x: 35, y: 25 },  // stay deep half
    ],
  },
  {
    routeTrigger: 'out',
    response: [
      { x: 30, y: 35 },  // backpedal
      { x: 28, y: 25 },  // stay deep half
    ],
  },
  {
    routeTrigger: 'deepOut',
    response: [
      { x: 28, y: 35 },  // backpedal
      { x: 20, y: 25 },  // break on deep out
    ],
  },
  {
    routeTrigger: 'deepIn',
    response: [
      { x: 32, y: 35 },  // backpedal
      { x: 45, y: 30 },  // drive on dig
    ],
  },
  {
    routeTrigger: 'comeback',
    response: [
      { x: 28, y: 32 },  // backpedal
      { x: 22, y: 28 },  // break on comeback
    ],
  },
  {
    routeTrigger: 'go',
    response: [
      { x: 28, y: 30 },  // backpedal
      { x: 22, y: 12 },  // run with go route
    ],
  },
  {
    routeTrigger: 'post',
    response: [
      { x: 32, y: 32 },  // backpedal
      { x: 42, y: 18 },  // drive on post
    ],
  },
  {
    routeTrigger: 'corner',
    response: [
      { x: 28, y: 32 },  // backpedal
      { x: 15, y: 20 },  // drive on corner
    ],
  },
  {
    // Drag: Short route - stay deep
    routeTrigger: 'drag',
    response: [
      { x: 30, y: 35 },
      { x: 30, y: 25 },
    ],
  },
  {
    // Hitch: Short route - stay deep
    routeTrigger: 'hitch',
    response: [
      { x: 30, y: 35 },
      { x: 28, y: 25 },
    ],
  },
  {
    // Curl: 10yd stop - backpedal, break on curl
    routeTrigger: 'curl',
    response: [
      { x: 28, y: 32 },
      { x: 22, y: 28 },
    ],
  },
];

/**
 * CB2 reactions - mirrors CB1 for right side
 */
const cover2CB2Reactions: CB2Reaction[] = [
  {
    routeTrigger: 'slant',
    wrPositionTrigger: { x: 80, y: 75 },
    cbResponse: [
      { x: 82, y: 72 },
      { x: 88, y: 65 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'out',
    wrPositionTrigger: { x: 80, y: 70 },
    cbResponse: [
      { x: 85, y: 72 },
      { x: 92, y: 68 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'deepOut',
    wrPositionTrigger: { x: 80, y: 45 },
    cbResponse: [
      { x: 85, y: 70 },
      { x: 90, y: 55 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'deepIn',
    wrPositionTrigger: { x: 80, y: 45 },
    cbResponse: [
      { x: 82, y: 70 },
      { x: 88, y: 60 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'comeback',
    wrPositionTrigger: { x: 80, y: 50 },
    cbResponse: [
      { x: 85, y: 68 },
      { x: 88, y: 55 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'go',
    wrPositionTrigger: { x: 80, y: 70 },
    cbResponse: [
      { x: 82, y: 70 },
      { x: 85, y: 55 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'post',
    wrPositionTrigger: { x: 80, y: 55 },
    cbResponse: [
      { x: 82, y: 70 },
      { x: 88, y: 58 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'corner',
    wrPositionTrigger: { x: 80, y: 55 },
    cbResponse: [
      { x: 85, y: 68 },
      { x: 90, y: 50 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'drag',
    wrPositionTrigger: { x: 80, y: 80 },
    cbResponse: [
      { x: 85, y: 75 },
      { x: 88, y: 68 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'hitch',
    wrPositionTrigger: { x: 80, y: 70 },
    cbResponse: [
      { x: 85, y: 72 },
      { x: 90, y: 68 },
    ],
    responseDelay: 0,
  },
  {
    routeTrigger: 'curl',
    wrPositionTrigger: { x: 80, y: 55 },
    cbResponse: [
      { x: 85, y: 68 },
      { x: 88, y: 55 },
    ],
    responseDelay: 0,
  },
];

/**
 * DB/LB2 reactions - mirrors DB/LB1
 */
const cover2DBLB2Reactions: DBLB2Reaction[] = [
  {
    routeTrigger: 'slant',
    response: [
      { x: 70, y: 65 },
      { x: 65, y: 55 },
    ],
  },
  {
    routeTrigger: 'out',
    response: [
      { x: 72, y: 65 },
      { x: 75, y: 58 },
    ],
  },
  {
    routeTrigger: 'deepOut',
    response: [
      { x: 72, y: 62 },
      { x: 75, y: 50 },
    ],
  },
  {
    routeTrigger: 'deepIn',
    response: [
      { x: 70, y: 62 },
      { x: 60, y: 48 },
    ],
  },
  {
    routeTrigger: 'comeback',
    response: [
      { x: 72, y: 60 },
      { x: 78, y: 48 },
    ],
  },
  {
    routeTrigger: 'go',
    response: [
      { x: 70, y: 62 },
      { x: 70, y: 50 },
    ],
  },
  {
    routeTrigger: 'post',
    response: [
      { x: 70, y: 62 },
      { x: 62, y: 50 },
    ],
  },
  {
    routeTrigger: 'corner',
    response: [
      { x: 72, y: 62 },
      { x: 78, y: 50 },
    ],
  },
  {
    routeTrigger: 'drag',
    response: [
      { x: 70, y: 65 },
      { x: 65, y: 55 },
    ],
  },
  {
    routeTrigger: 'hitch',
    response: [
      { x: 72, y: 65 },
      { x: 75, y: 58 },
    ],
  },
  {
    routeTrigger: 'curl',
    response: [
      { x: 72, y: 62 },
      { x: 76, y: 50 },
    ],
  },
];

/**
 * SS reactions - deep half safety (right side)
 */
const cover2SSReactions: SSReaction[] = [
  {
    routeTrigger: 'slant',
    response: [
      { x: 70, y: 35 },
      { x: 65, y: 25 },
    ],
  },
  {
    routeTrigger: 'out',
    response: [
      { x: 70, y: 35 },
      { x: 72, y: 25 },
    ],
  },
  {
    routeTrigger: 'deepOut',
    response: [
      { x: 72, y: 35 },
      { x: 80, y: 25 },
    ],
  },
  {
    routeTrigger: 'deepIn',
    response: [
      { x: 68, y: 35 },
      { x: 55, y: 30 },
    ],
  },
  {
    routeTrigger: 'comeback',
    response: [
      { x: 72, y: 32 },
      { x: 78, y: 28 },
    ],
  },
  {
    routeTrigger: 'go',
    response: [
      { x: 72, y: 30 },
      { x: 78, y: 12 },
    ],
  },
  {
    routeTrigger: 'post',
    response: [
      { x: 68, y: 32 },
      { x: 58, y: 18 },
    ],
  },
  {
    routeTrigger: 'corner',
    response: [
      { x: 72, y: 32 },
      { x: 85, y: 20 },
    ],
  },
  {
    routeTrigger: 'drag',
    response: [
      { x: 70, y: 35 },
      { x: 70, y: 25 },
    ],
  },
  {
    routeTrigger: 'hitch',
    response: [
      { x: 70, y: 35 },
      { x: 72, y: 25 },
    ],
  },
  {
    routeTrigger: 'curl',
    response: [
      { x: 72, y: 32 },
      { x: 78, y: 28 },
    ],
  },
];

/**
 * LB reactions - middle zone (hole) defender
 */
const cover2LBReactions: LBReaction[] = [
  {
    routeTrigger: 'out',
    response: [
      { x: 50, y: 65 },
      { x: 50, y: 55 },
    ],
  },
  {
    routeTrigger: 'swing',
    response: [
      { x: 50, y: 65 },
      { x: 55, y: 58 },
    ],
  },
  {
    routeTrigger: 'curl',
    response: [
      { x: 50, y: 65 },
      { x: 52, y: 55 },
    ],
  },
  {
    routeTrigger: 'wheel',
    response: [
      { x: 50, y: 65 },
      { x: 55, y: 55 },
      { x: 60, y: 45 },
    ],
  },
];

/**
 * Complete Cover 2 definition
 */
export const cover2Definition: CoverageDefinition = {
  id: 'cover2',
  name: 'Cover 2',
  description: 'Two deep safeties, CBs in flat zones',
  initialPosition: CB1_COVER2_START,
  reactionRules: cover2Reactions,
  dblb1InitialPosition: DBLB1_COVER2_START,
  dblb1ReactionRules: cover2DBLB1Reactions,
  fsInitialPosition: FS_COVER2_START,
  fsReactionRules: cover2FSReactions,
  cb2InitialPosition: CB2_COVER2_START,
  cb2ReactionRules: cover2CB2Reactions,
  dblb2InitialPosition: DBLB2_COVER2_START,
  dblb2ReactionRules: cover2DBLB2Reactions,
  ssInitialPosition: SS_COVER2_START,
  ssReactionRules: cover2SSReactions,
  lbInitialPosition: LB_COVER2_START,
  lbReactionRules: cover2LBReactions,
};

export function getCover2StartPosition(): Position {
  return { ...CB1_COVER2_START };
}

export function getCover2Definition(): CoverageDefinition {
  return { ...cover2Definition };
}

export { 
  CB1_COVER2_START, 
  DBLB1_COVER2_START, 
  FS_COVER2_START, 
  CB2_COVER2_START, 
  DBLB2_COVER2_START, 
  SS_COVER2_START, 
  LB_COVER2_START,
  cover2Reactions, 
  cover2DBLB1Reactions, 
  cover2FSReactions, 
  cover2CB2Reactions, 
  cover2DBLB2Reactions, 
  cover2SSReactions,
  cover2LBReactions
};
